import { useCallback } from 'react';
import { personnelPermitsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { PersonnelCoastGuardPermit } from '../types';

export function usePersonnelPermits(
  options?: ResourceQueryOptions
): ResourceState<PersonnelCoastGuardPermit> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      personnelPermitsService.getAll(opts) as unknown as Promise<{ data: PersonnelCoastGuardPermit[]; meta?: any }>,
    []
  );
  return useResourceCollection<PersonnelCoastGuardPermit>(fetchFn, options);
}

export function usePersonnelPermit(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<PersonnelCoastGuardPermit> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (permitId: number, opts?: ResourceQueryOptions) =>
      personnelPermitsService.getById(permitId, opts) as unknown as Promise<PersonnelCoastGuardPermit>,
    []
  );
  return useResourceById<PersonnelCoastGuardPermit>(fetchFn, numericId, options);
}

export function useCreatePersonnelPermit(
  onSuccess?: (data: PersonnelCoastGuardPermit) => void
): MutationState<PersonnelCoastGuardPermit> {
  const createFn = useCallback(
    (data: any) => personnelPermitsService.create(data) as unknown as Promise<PersonnelCoastGuardPermit>,
    []
  );
  return useCreateResource<PersonnelCoastGuardPermit>(createFn, onSuccess);
}

export function useUpdatePersonnelPermit(
  onSuccess?: (data: PersonnelCoastGuardPermit) => void
): MutationState<PersonnelCoastGuardPermit> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return personnelPermitsService.update(numericId, data) as unknown as Promise<PersonnelCoastGuardPermit>;
    },
    []
  );
  return useUpdateResource<PersonnelCoastGuardPermit>(updateFn, onSuccess);
}

export function useDeletePersonnelPermit(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return personnelPermitsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

